import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, CheckCircle, Info, Loader2, Plus, Minus, Save, X } from 'lucide-react';
import { templateService } from '../../services/templateService';
import toast from 'react-hot-toast';

const EditTemplatePage = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [nameExists, setNameExists] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [variables, setVariables] = useState([]);
  const [headerVariables, setHeaderVariables] = useState([]);
  const [originalName, setOriginalName] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue
  } = useForm({
    defaultValues: {
      templateName: '',
      language: 'en_US',
      templateCategory: 'MARKETING',
      content: '',
      templateStatus: 'DRAFT',
      sampleText: '',
      headerText: '',
      headerSampleText: '',
      variableSamples: {},
      headerVariableSamples: {}
    }
  });

  const watchedContent = watch('content');
  const watchedName = watch('templateName');
  const watchedHeaderText = watch('headerText');
  
  // Check if content has variables
  const hasVariables = watchedContent && watchedContent.includes('{{');
  const hasHeaderVariables = watchedHeaderText && watchedHeaderText.includes('{{');

  // Extract variables from text
  const extractVariables = (text) => {
    if (!text) return [];
    const matches = text.match(/\{\{(\d+)\}\}/g);
    if (!matches) return [];
    
    return matches.map(match => {
      const num = match.match(/\{(\d+)\}/)[1];
      return parseInt(num);
    }).sort((a, b) => a - b);
  };

  // Load template data
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        setIsLoading(true);
          const template = await templateService.getTemplate(templateId);
        
        // Set form values
        reset({
          templateName: template.templateName,
          language: template.language,
          templateCategory: template.templateCategory,
          content: template.content,
          templateStatus: template.templateStatus,
          sampleText: template.sampleText || '',
          headerText: template.headerText || '',
          headerSampleText: template.headerSampleText || '',
          variableSamples: template.variableSamples || {},
          headerVariableSamples: template.headerVariableSamples || {}
        });

        setOriginalName(template.templateName);
        
        // Extract variables
        if (template.content) {
          const contentVars = extractVariables(template.content);
          setVariables(contentVars);
        }
        
        if (template.headerText) {
          const headerVars = extractVariables(template.headerText);
          setHeaderVariables(headerVars);
        }
        
      } catch (error) {
        toast.error('Failed to load template');
        navigate('/template-manager');
      } finally {
        setIsLoading(false);
      }
    };

    if (templateId) {
      loadTemplate();
    }
  }, [templateId, navigate, reset]);

  // Update variables when content changes
  useEffect(() => {
    if (watchedContent) {
      const newVariables = extractVariables(watchedContent);
      setVariables(newVariables);
      
      // Clear variable samples for removed variables
      const currentSamples = {};
      newVariables.forEach(varNum => {
        const currentValue = watch(`variable_${varNum}`);
        if (currentValue) {
          currentSamples[varNum] = currentValue;
        }
      });
      setValue('variableSamples', currentSamples);
    } else {
      setVariables([]);
      setValue('variableSamples', {});
    }
  }, [watchedContent, setValue, watch]);

  // Update header variables when header text changes
  useEffect(() => {
    if (watchedHeaderText) {
      const newHeaderVariables = extractVariables(watchedHeaderText);
      setHeaderVariables(newHeaderVariables);
      
      // Clear header variable samples for removed variables
      const currentHeaderSamples = {};
      newHeaderVariables.forEach(varNum => {
        const currentValue = watch(`headerVariable_${varNum}`);
        if (currentValue) {
          currentHeaderSamples[varNum] = currentValue;
        }
      });
      setValue('headerVariableSamples', currentHeaderSamples);
    } else {
      setHeaderVariables([]);
      setValue('headerVariableSamples', {});
    }
  }, [watchedHeaderText, setValue, watch]);

  // Check if template name exists (only if different from original)
  const checkNameExists = async (name) => {
    if (!name || name.length < 3 || name === originalName) {
      setNameExists(false);
      return;
    }

    setIsCheckingName(true);
    try {
      const exists = await templateService.checkTemplateName(name);
      setNameExists(exists);
    } catch (error) {
      console.error('Error checking name:', error);
      setNameExists(false);
    } finally {
      setIsCheckingName(false);
    }
  };

  // Debounced name check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkNameExists(watchedName);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [watchedName, originalName]);

  // Generate preview content
  useEffect(() => {
    if (!watchedContent) {
      setPreviewContent('');
      return;
    }

    let preview = watchedContent;
    
    // Replace variables with sample values
    variables.forEach(varNum => {
      const sampleValue = watch(`variable_${varNum}`);
      if (sampleValue) {
        preview = preview.replace(new RegExp(`\\{\\{${varNum}\\}\\}`, 'g'), sampleValue);
      }
    });

    setPreviewContent(preview);
  }, [watchedContent, variables, watch]);

  const addBodyVariable = () => {
    const nextVarNum = Math.max(0, ...variables) + 1;
    const currentContent = watch('content') || '';
    const newContent = currentContent + `{{${nextVarNum}}}`;
    setValue('content', newContent);
    setVariables([...variables, nextVarNum]);
  };

  const removeBodyVariable = (varNum) => {
    // Remove variable from the content text
    const currentContent = watch('content') || '';
    const newContent = currentContent.replace(new RegExp(`\\{\\{${varNum}\\}\\}`, 'g'), '');
    setValue('content', newContent);
    
    // Remove from variables array and clear the sample value
    setVariables(variables.filter(v => v !== varNum));
    setValue(`variable_${varNum}`, '');
  };

  const addHeaderVariable = () => {
    const nextVarNum = Math.max(0, ...headerVariables) + 1;
    const currentHeader = watch('headerText') || '';
    const newHeader = currentHeader + `{{${nextVarNum}}}`;
    setValue('headerText', newHeader);
    setHeaderVariables([...headerVariables, nextVarNum]);
  };

  const removeHeaderVariable = (varNum) => {
    // Remove variable from the header text
    const currentHeader = watch('headerText') || '';
    const newHeader = currentHeader.replace(new RegExp(`\\{\\{${varNum}\\}\\}`, 'g'), '');
    setValue('headerText', newHeader);
    
    // Remove from header variables array and clear the sample value
    setHeaderVariables(headerVariables.filter(v => v !== varNum));
    setValue(`headerVariable_${varNum}`, '');
  };

  const onSubmit = async (data) => {
    if (nameExists) {
      toast.error('Template name already exists');
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare variable samples
      const variableSamples = {};
      variables.forEach(varNum => {
        const value = data[`variable_${varNum}`];
        if (value) {
          variableSamples[varNum] = value;
        }
      });

      // Prepare header variable samples
      const headerVariableSamples = {};
      headerVariables.forEach(varNum => {
        const value = data[`headerVariable_${varNum}`];
        if (value) {
          headerVariableSamples[varNum] = value;
        }
      });

      const templateData = {
        ...data,
        variableSamples,
        headerVariableSamples
      };

      await templateService.updateTemplate(templateId, templateData);
      toast.success('Template updated successfully');
      navigate('/template-manager');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update template');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/template-manager');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Edit Template</h1>
                <p className="text-sm text-gray-600">Update your WhatsApp message template</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleCancel}
                className="bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                form="template-form"
                type="submit"
                disabled={isSubmitting || nameExists}
                className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {isSubmitting ? 'Updating...' : 'Update Template'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Template Details</h2>
                <p className="text-sm text-gray-600 mt-1">Update the template information below</p>
              </div>

              <form id="template-form" onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                {/* Template Name */}
                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-2">
                    Template Name *
                  </label>
                  <div className="relative">
                    <input
                      {...register('templateName', {
                        required: 'Template name is required',
                        maxLength: {
                          value: 100,
                          message: 'Template name must be less than 100 characters'
                        },
                        pattern: {
                          value: /^[a-zA-Z0-9\s_-]+$/,
                          message: 'Template name can only contain letters, numbers, spaces, hyphens, and underscores'
                        }
                      })}
                      type="text"
                      className={`input-field ${errors.templateName ? 'border-red-500' : ''} ${nameExists ? 'border-red-500' : ''}`}
                      placeholder="Enter template name"
                      disabled={isSubmitting}
                    />
                    {isCheckingName && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      </div>
                    )}
                    {nameExists && !isCheckingName && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <X className="h-4 w-4 text-red-500" />
                      </div>
                    )}
                    {!nameExists && !isCheckingName && watchedName && watchedName.length >= 3 && watchedName !== originalName && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      </div>
                    )}
                  </div>
                  {errors.templateName && (
                    <p className="mt-1 text-sm text-red-600">{errors.templateName.message}</p>
                  )}
                  {nameExists && (
                    <p className="mt-1 text-sm text-red-600">Template name already exists</p>
                  )}
                </div>

                {/* Language and Category */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      Language *
                    </label>
                    <select
                      {...register('language', { required: 'Language is required' })}
                      className={`input-field ${errors.language ? 'border-red-500' : ''}`}
                      disabled={isSubmitting}
                    >
                      <option value="en_US">English (US)</option>
                      <option value="en_GB">English (UK)</option>
                      <option value="es_ES">Spanish (Spain)</option>
                      <option value="es_MX">Spanish (Mexico)</option>
                      <option value="fr_FR">French (France)</option>
                      <option value="de_DE">German (Germany)</option>
                      <option value="it_IT">Italian (Italy)</option>
                      <option value="pt_BR">Portuguese (Brazil)</option>
                      <option value="hi_IN">Hindi (India)</option>
                      <option value="ar_SA">Arabic (Saudi Arabia)</option>
                    </select>
                    {errors.language && (
                      <p className="mt-1 text-sm text-red-600">{errors.language.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      Category *
                    </label>
                    <select
                      {...register('templateCategory', { required: 'Category is required' })}
                      className={`input-field ${errors.templateCategory ? 'border-red-500' : ''}`}
                      disabled={isSubmitting}
                    >
                      <option value="MARKETING">Marketing</option>
                      <option value="UTILITY">Utility</option>
                      <option value="AUTHENTICATION">Authentication</option>
                    </select>
                    {errors.templateCategory && (
                      <p className="mt-1 text-sm text-red-600">{errors.templateCategory.message}</p>
                    )}
                  </div>
                </div>

                {/* Template Content */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-bold text-gray-800">
                      Template Content *
                    </label>
                    <button
                      type="button"
                      onClick={addBodyVariable}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      disabled={isSubmitting}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Variable
                    </button>
                  </div>
                  <textarea
                    {...register('content', {
                      required: 'Template content is required',
                      maxLength: {
                        value: 1000,
                        message: 'Content must be less than 1000 characters'
                      }
                    })}
                    rows={6}
                    className={`input-field ${errors.content ? 'border-red-500' : ''}`}
                    placeholder="Enter your template content. Use {{1}}, {{2}}, etc. for variables."
                    disabled={isSubmitting}
                  />
                  {errors.content && (
                    <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
                  )}
                  
                  {variables.length > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800 mb-3">
                        <Info className="h-4 w-4 inline mr-1" />
                        Variables in your content. Please provide sample values for each variable.
                      </p>
                      
                      <div className="space-y-3">
                        {variables.map(varNum => (
                          <div key={varNum} className="flex items-center space-x-2">
                            <div className="flex-1">
                              <label className="block text-sm font-bold text-gray-800 mb-1">
                                Sample for {`{{${varNum}}}`} *
                              </label>
                              <input
                                {...register(`variable_${varNum}`, {
                                  required: `Sample text for variable {{${varNum}}} is required`,
                                  maxLength: {
                                    value: 100,
                                    message: 'Sample text must be less than 100 characters'
                                  }
                                })}
                                type="text"
                                className={`input-field ${errors[`variable_${varNum}`] ? 'border-red-500' : ''}`}
                                placeholder={`Enter sample text for {{${varNum}}}`}
                                disabled={isSubmitting}
                              />
                              {errors[`variable_${varNum}`] && (
                                <p className="mt-1 text-sm text-red-600">{errors[`variable_${varNum}`].message}</p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeBodyVariable(varNum)}
                              className="text-red-600 hover:text-red-800 p-1"
                              disabled={isSubmitting}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Header Text */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-bold text-gray-800">
                      Header Text (Optional)
                    </label>
                    <button
                      type="button"
                      onClick={addHeaderVariable}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      disabled={isSubmitting}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Variable
                    </button>
                  </div>
                  <textarea
                    {...register('headerText', {
                      maxLength: {
                        value: 60,
                        message: 'Header text must be less than 60 characters'
                      }
                    })}
                    rows={3}
                    className={`input-field ${errors.headerText ? 'border-red-500' : ''}`}
                    placeholder="Enter header text (optional). Use {{1}}, {{2}}, etc. for variables."
                    disabled={isSubmitting}
                  />
                  {errors.headerText && (
                    <p className="mt-1 text-sm text-red-600">{errors.headerText.message}</p>
                  )}
                  
                  {headerVariables.length > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800 mb-3">
                        <Info className="h-4 w-4 inline mr-1" />
                        Header variables in your content. Please provide sample values for each variable.
                      </p>
                      
                      <div className="space-y-3">
                        {headerVariables.map(varNum => (
                          <div key={varNum} className="flex items-center space-x-2">
                            <div className="flex-1">
                              <label className="block text-sm font-bold text-gray-800 mb-1">
                                Sample for Header {`{{${varNum}}}`} *
                              </label>
                              <input
                                {...register(`headerVariable_${varNum}`, {
                                  required: `Sample text for header variable {{${varNum}}} is required`,
                                  maxLength: {
                                    value: 100,
                                    message: 'Sample text must be less than 100 characters'
                                  }
                                })}
                                type="text"
                                className={`input-field ${errors[`headerVariable_${varNum}`] ? 'border-red-500' : ''}`}
                                placeholder={`Enter sample text for header {{${varNum}}}`}
                                disabled={isSubmitting}
                              />
                              {errors[`headerVariable_${varNum}`] && (
                                <p className="mt-1 text-sm text-red-600">{errors[`headerVariable_${varNum}`].message}</p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeHeaderVariable(varNum)}
                              className="text-red-600 hover:text-red-800 p-1"
                              disabled={isSubmitting}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Sample Text */}
                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-2">
                    Sample Text (Optional)
                  </label>
                  <textarea
                    {...register('sampleText', {
                      maxLength: {
                        value: 200,
                        message: 'Sample text must be less than 200 characters'
                      }
                    })}
                    rows={3}
                    className={`input-field ${errors.sampleText ? 'border-red-500' : ''}`}
                    placeholder="Enter sample text to show how the template will look"
                    disabled={isSubmitting}
                  />
                  {errors.sampleText && (
                    <p className="mt-1 text-sm text-red-600">{errors.sampleText.message}</p>
                  )}
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || nameExists}
                    className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {isSubmitting ? 'Updating...' : 'Update Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Guidelines Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* WhatsApp Guidelines */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Info className="h-5 w-5 mr-2 text-blue-600" />
                    WhatsApp Guidelines
                  </h3>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Template Name</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Use descriptive, clear names</li>
                      <li>• Avoid special characters</li>
                      <li>• Keep it under 100 characters</li>
                      <li>• Use underscores or hyphens for spaces</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Content Rules</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• No promotional content in utility templates</li>
                      <li>• Clear call-to-action for marketing</li>
                      <li>• Professional tone and language</li>
                      <li>• Avoid spam-like content</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Variables</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Use {'{{1}}'}, {'{{2}}'}, {'{{3}}'} format</li>
                      <li>• Provide meaningful sample values</li>
                      <li>• Keep samples under 100 characters</li>
                      <li>• Use realistic examples</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Header Text</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Optional but recommended</li>
                      <li>• Keep under 60 characters</li>
                      <li>• Use for important information</li>
                      <li>• Can include variables</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Preview */}
              {previewContent && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Preview</h3>
                  </div>
                  <div className="p-4">
                    <div className="bg-gray-50 border rounded-lg p-3">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{previewContent}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Tips */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Pro Tips
                </h4>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• Test your template with real data</li>
                  <li>• Keep messages concise and clear</li>
                  <li>• Use appropriate category for your use case</li>
                  <li>• Review WhatsApp's policy before submitting</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTemplatePage;
